import {
    Box,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    alpha,
} from '@mui/material';
import { ReactNode, memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import {
    type CustomListItem,
    getCustomListCatalogItem,
} from 'entities/CustomList';
import openBoxSearch from 'shared/assets/svg/open-box-search.svg';
import {
    CatalogProductTable,
    CatalogProductTableRow,
    catalogProductColumnWidths,
    catalogProductItemHeaderCellSx,
} from 'shared/ui/CatalogProductTable';
import { EmptyPlaceholder } from 'shared/ui/EmptyPlaceholder/EmptyPlaceholder';
import { InfoHintIcon } from 'shared/ui/Tooltip';
import { ProductDetailsDrawer } from 'widgets/ProductDetailsDrawer';

import { OrderAddItemsListsTableRowSkeleton } from './OrderAddItemsListsTableRowSkeleton';
import {
    type OrderAddItemsListsSortKey,
    useOrderAddItemsListsTableItems,
} from './useOrderAddItemsListsTableItems';

import { OrderListItemAddCell } from '../OrderListItemAddCell/OrderListItemAddCell';

interface OrderAddItemsListsTableProps {
    items: CustomListItem[];
    itemSearch: string;
    categoryFilter: string;
    listId: string;
    isLoading?: boolean;
}

export const OrderAddItemsListsTable = memo(
    ({
        items,
        itemSearch,
        categoryFilter,
        listId,
        isLoading = false,
    }: OrderAddItemsListsTableProps) => {
        const { t } = useTranslation();
        const { tableItems, sortKey, sortDir, onSort } =
            useOrderAddItemsListsTableItems(items, itemSearch, categoryFilter);
        const showNoResults = items.length > 0 && tableItems.length === 0;
        const [openItem, setOpenItem] = useState<CatalogItem | null>(null);

        const onOpenItem = useCallback((listItem: CustomListItem) => {
            const catalogItem = getCustomListCatalogItem(listItem);
            if (!catalogItem) {
                return;
            }
            setOpenItem(catalogItem);
        }, []);

        const onCloseItem = useCallback(() => {
            setOpenItem(null);
        }, []);

        const sortLabel = (
            key: OrderAddItemsListsSortKey,
            label: string,
            info?: ReactNode,
        ) => (
            <TableSortLabel
                active={sortKey === key}
                direction={sortKey === key ? sortDir : 'asc'}
                onClick={() => onSort(key)}
                sx={{
                    position: 'relative',
                    '& .MuiTableSortLabel-icon': {
                        position: 'absolute',
                        left: '100%',
                        marginLeft: '4px',
                        marginRight: 0,
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                    }}
                >
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {label}
                    </Typography>
                    {info}
                </Box>
            </TableSortLabel>
        );

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    height: '100%',
                }}
                data-testclass="order-add-items-lists-table"
            >
                <CatalogProductTable>
                    <TableHead
                        sx={{
                            '& th': {
                                paddingTop: 0,
                                paddingBottom: 0,
                                bgcolor: 'transparent',
                            },
                        }}
                    >
                        <TableRow>
                            <TableCell sx={catalogProductItemHeaderCellSx}>
                                {sortLabel('item', t('Item'))}
                            </TableCell>
                            <TableCell
                                align="left"
                                width={catalogProductColumnWidths.category}
                            >
                                {sortLabel('category', t('Category'))}
                            </TableCell>
                            <TableCell
                                align="center"
                                width={catalogProductColumnWidths.uom}
                            >
                                {sortLabel('uom', t('UOM'))}
                            </TableCell>
                            <TableCell
                                align="center"
                                width={catalogProductColumnWidths.defaultQty}
                            >
                                {sortLabel(
                                    'defaultQty',
                                    t('customLists.defaultQty'),
                                    <InfoHintIcon
                                        content={t(
                                            'customLists.defaultQtyInfoTooltip',
                                        )}
                                        dataTestClass="order-add-items-default-qty-info"
                                    />,
                                )}
                            </TableCell>
                            <TableCell
                                width={catalogProductColumnWidths.orderAdd}
                            />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {showNoResults ? (
                            <TableRow
                                aria-hidden
                                sx={{
                                    visibility: 'collapse',
                                    '& td': {
                                        paddingTop: 0,
                                        paddingBottom: 0,
                                        border: 0,
                                        lineHeight: 0,
                                    },
                                }}
                            >
                                <TableCell
                                    sx={{
                                        paddingLeft: '16px',
                                        paddingRight: '16px',
                                        width: catalogProductColumnWidths.item,
                                        maxWidth:
                                            catalogProductColumnWidths.item,
                                        boxSizing: 'border-box',
                                    }}
                                />
                                <TableCell
                                    sx={{
                                        width: catalogProductColumnWidths.category,
                                        boxSizing: 'border-box',
                                    }}
                                />
                                <TableCell
                                    sx={{
                                        width: catalogProductColumnWidths.uom,
                                        boxSizing: 'border-box',
                                    }}
                                />
                                <TableCell
                                    sx={{
                                        width: catalogProductColumnWidths.defaultQty,
                                        boxSizing: 'border-box',
                                    }}
                                />
                                <TableCell
                                    sx={{
                                        width: catalogProductColumnWidths.orderAdd,
                                        boxSizing: 'content-box',
                                        paddingRight: '12px',
                                    }}
                                />
                            </TableRow>
                        ) : null}
                        {isLoading
                            ? Array.from({ length: 5 }).map((_, index) => (
                                  <OrderAddItemsListsTableRowSkeleton
                                      key={index}
                                  />
                              ))
                            : tableItems.map((listItem) => {
                                  const catalogItem =
                                      getCustomListCatalogItem(listItem);
                                  const defaultQty =
                                      listItem.targetQuantity || 1;

                                  return (
                                      <CatalogProductTableRow
                                          key={listItem.id}
                                          product={{
                                              title: catalogItem?.title,
                                              thumbnailUrl:
                                                  catalogItem?.thumbnailUrl,
                                              imageUrl: catalogItem?.imageUrl,
                                              categoryId:
                                                  catalogItem?.categoryId,
                                              uom: catalogItem?.uom,
                                              proposals: catalogItem?.proposals,
                                          }}
                                          showPrice={false}
                                          onClick={
                                              catalogItem
                                                  ? () => onOpenItem(listItem)
                                                  : undefined
                                          }
                                          quantityCell={
                                              <Typography
                                                  variant="body1"
                                                  align="center"
                                              >
                                                  {defaultQty}
                                              </Typography>
                                          }
                                          trailing={
                                              <OrderListItemAddCell
                                                  listItem={listItem}
                                                  listId={listId}
                                              />
                                          }
                                          trailingColumnWidth={
                                              catalogProductColumnWidths.orderAdd
                                          }
                                          dataTestClass="order-add-items-list-item-row"
                                      />
                                  );
                              })}
                    </TableBody>
                </CatalogProductTable>
                {showNoResults ? (
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 0,
                        }}
                        data-testclass="order-add-items-lists-no-results"
                    >
                        <EmptyPlaceholder
                            image={openBoxSearch}
                            childrenContainerSx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                color={(theme) =>
                                    alpha(theme.palette.common.black, 0.5)
                                }
                                variant="subtitle2"
                                sx={{
                                    maxWidth: '280px',
                                    textAlign: 'center',
                                }}
                            >
                                {t('No items found in this list')}.
                            </Typography>
                        </EmptyPlaceholder>
                    </Box>
                ) : null}
                <ProductDetailsDrawer
                    open={openItem !== null}
                    onClose={onCloseItem}
                    catalogItem={openItem}
                />
            </Box>
        );
    },
);

OrderAddItemsListsTable.displayName = 'OrderAddItemsListsTable';
